import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {CanvasEnvironment} from '../../../../utils/canvas/canvas-environment';
import {pickContrastingTextColor} from '../../../../utils/canvas/colors';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';
import {
    AdjustedCutSet,
    applyCCFAnalysis,
    CCFAnalysisResult,
    CCFGroup,
    CCFGroupDetected,
    CCFGroupParams,
    CCFModelType,
    detectCCFGroups,
    MinimalCutSetsResult,
} from '../../../../utils/minimal-cut-sets';
import {escapeCSVValue, formatTimestamp} from '../../../../utils/export/unifiedReportExporter';

type SortField = 'index' | 'order' | 'probability' | 'adjustedProbability';
type SortDirection = 'asc' | 'desc';

function formatProbability(value: number): string {
    if (value === 0) return '0';
    if (value < 0.001) return value.toExponential(3);
    return value.toPrecision(4);
}

function buildDefaultParams(model: CCFModelType, memberCount: number): CCFGroupParams | null {
    switch (model) {
        case 'beta-factor':
            return { model: 'beta-factor', beta: 0.1 };
        case 'alpha-factor': {
            const alphas = new Array(memberCount).fill(0);
            alphas[0] = 0.98;
            alphas[memberCount - 1] = 0.02;
            return { model: 'alpha-factor', alphas };
        }
        case 'mgl':
            return { model: 'mgl', beta: 0.1, gamma: 0.5, delta: 0.5 };
        default:
            return null;
    }
}

export interface CCFAnalysisProps {
    result: MinimalCutSetsResult;
    nodeNameMap: Map<number, string>;
    nodeMap: Map<number, FaultTreeNode>;
    env: CanvasEnvironment;
    onRowClick: (index: number) => void;
    selectedIndex: number | null;
    onExportReady: (handler: (() => void) | null) => void;
    onGroupCountChange: (count: number) => void;
}

const CCFAnalysis: React.FC<CCFAnalysisProps> = ({
    result,
    nodeNameMap,
    nodeMap,
    env,
    onRowClick,
    selectedIndex,
    onExportReady,
    onGroupCountChange,
}) => {
    const [ccfModelType, setCcfModelType] = useState<CCFModelType>('none');
    const [detectedGroups, setDetectedGroups] = useState<CCFGroupDetected[]>([]);
    const [groupParams, setGroupParams] = useState<Map<number, CCFGroupParams>>(new Map());
    const [ccfResult, setCcfResult] = useState<CCFAnalysisResult | null>(null);
    const [sortField, setSortField] = useState<SortField>('index');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Detect CCF groups when result or env tags change
    useEffect(() => {
        const groups = detectCCFGroups(result.cutSets, env.tagLinksByFtNodeId, env.tagById);
        setDetectedGroups(groups);
        onGroupCountChange(groups.length);

        // Reset CCF state on new result
        setCcfModelType('none');
        setGroupParams(new Map());
        setCcfResult(null);
        setSortField('index');
        setSortDirection('asc');
    }, [result, env.tagLinksByFtNodeId, env.tagById, onGroupCountChange]);

    const handleSort = (field: SortField): void => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(field === 'probability' || field === 'adjustedProbability' ? 'desc' : 'asc');
        }
    };

    const handleCCFModelChange = useCallback((model: CCFModelType): void => {
        setCcfModelType(model);
        setCcfResult(null);
        if (model === 'none') {
            setGroupParams(new Map());
            return;
        }
        const newParams = new Map<number, CCFGroupParams>();
        for (const group of detectedGroups) {
            const params = buildDefaultParams(model, group.memberCount);
            if (params) {
                newParams.set(group.tagId, params);
            }
        }
        setGroupParams(newParams);
    }, [detectedGroups]);

    const handleGroupParamChange = useCallback((tagId: number, updatedParams: CCFGroupParams): void => {
        setGroupParams((prev) => {
            const next = new Map(prev);
            next.set(tagId, updatedParams);
            return next;
        });
        setCcfResult(null);
    }, []);

    const handleApplyCCF = useCallback((): void => {
        if (ccfModelType === 'none') return;
        const groups: CCFGroup[] = detectedGroups
            .map((detected) => {
                const params = groupParams.get(detected.tagId);
                if (!params) return null;
                return { detected, params };
            })
            .filter((g): g is CCFGroup => g !== null);
        const ccf = applyCCFAnalysis(result.cutSets, groups, nodeMap);
        setCcfResult(ccf);
    }, [result, ccfModelType, detectedGroups, groupParams, nodeMap]);

    const sortedAdjustedCutSets = useMemo((): Array<{ displayIndex: number | string; acs: AdjustedCutSet }> => {
        if (!ccfResult) return [];
        let indexCounter = 0;
        const indexed = ccfResult.adjustedCutSets.map((acs) => {
            if (acs.isImplicitCCF) {
                return { displayIndex: 'CCF' as number | string, acs };
            }
            indexCounter++;
            return { displayIndex: indexCounter as number | string, acs };
        });
        const dir = sortDirection === 'asc' ? 1 : -1;
        indexed.sort((a, b) => {
            switch (sortField) {
                case 'index': {
                    if (a.acs.isImplicitCCF !== b.acs.isImplicitCCF) return a.acs.isImplicitCCF ? -1 : 1;
                    return ((a.acs.originalIndex) - (b.acs.originalIndex)) * dir;
                }
                case 'order':
                    return (a.acs.order - b.acs.order) * dir;
                case 'probability':
                    return (a.acs.originalProbability - b.acs.originalProbability) * dir;
                case 'adjustedProbability':
                    return (a.acs.adjustedProbability - b.acs.adjustedProbability) * dir;
                default:
                    return 0;
            }
        });
        return indexed;
    }, [ccfResult, sortField, sortDirection]);

    const getBaseEventNames = useCallback((baseEventIds: Set<number>): string => {
        return Array.from(baseEventIds)
            .map((id) => nodeNameMap.get(id) || `ID:${id}`)
            .join(', ');
    }, [nodeNameMap]);

    const handleExportCSV = useCallback((): void => {
        if (!ccfResult) return;
        const header = ['#', 'Order', 'Probability', 'Adjusted Probability', 'CCF Implicit', 'CCF Group', 'Base Events']
            .map(escapeCSVValue).join(',');
        const rows = sortedAdjustedCutSets.map((row) =>
            [
                row.displayIndex,
                row.acs.order,
                row.acs.originalProbability,
                row.acs.adjustedProbability,
                row.acs.isImplicitCCF ? 'Yes' : 'No',
                escapeCSVValue(row.acs.ccfSourceTagName || ''),
                escapeCSVValue(getBaseEventNames(row.acs.baseEventIds)),
            ].join(',')
        );
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const rootName = result.rootNodeName.replace(/[^a-zA-Z0-9_-]/g, '_');
        link.setAttribute('href', url);
        link.setAttribute('download', `CCF_${rootName}_${formatTimestamp()}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [ccfResult, sortedAdjustedCutSets, getBaseEventNames, result.rootNodeName]);

    // Provide CSV export handler to parent
    useEffect(() => {
        if (!ccfResult) {
            onExportReady(null);
            return;
        }
        onExportReady(handleExportCSV);
    }, [ccfResult, onExportReady, handleExportCSV]);

    if (detectedGroups.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No CCF groups detected. Base events need shared tags (2+ base events with the same tag) to enable CCF analysis.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* CCF Controls */}
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <FormControl size="small" sx={{ minWidth: 180, mb: 1.5 }}>
                    <InputLabel>Model</InputLabel>
                    <Select
                        value={ccfModelType}
                        label="Model"
                        onChange={(e): void => handleCCFModelChange(e.target.value as CCFModelType)}
                    >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="beta-factor">Beta-Factor</MenuItem>
                        <MenuItem value="alpha-factor">Alpha-Factor</MenuItem>
                        <MenuItem value="mgl">MGL</MenuItem>
                    </Select>
                </FormControl>

                {ccfModelType !== 'none' && detectedGroups.map((group) => {
                    const params = groupParams.get(group.tagId);
                    if (!params) return null;
                    const bgColor = group.tagColor || '#9e9e9e';
                    const textColor = pickContrastingTextColor(bgColor);
                    return (
                        <Box
                            key={group.tagId}
                            sx={{
                                mb: 1.5,
                                p: 1.5,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Chip
                                    label={group.tagName}
                                    size="small"
                                    sx={{ bgcolor: bgColor, color: textColor }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {group.memberCount} base events
                                </Typography>
                            </Stack>

                            {/* Beta-Factor params */}
                            {params.model === 'beta-factor' && (
                                <Box>
                                    <TextField
                                        label={'\u03B2'}
                                        type="number"
                                        size="small"
                                        inputProps={{ min: 0, max: 1, step: 0.01 }}
                                        value={params.beta}
                                        onChange={(e): void => {
                                            const val = parseFloat(e.target.value);
                                            if (!isNaN(val)) {
                                                handleGroupParamChange(group.tagId, { model: 'beta-factor', beta: val });
                                            }
                                        }}
                                        sx={{ width: 100 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        Typical: 0.01–0.05 diverse/well-separated, 0.05–0.1 shared maintenance, 0.1–0.2 identical environment.
                                    </Typography>
                                </Box>
                            )}

                            {/* Alpha-Factor params */}
                            {params.model === 'alpha-factor' && (
                                <Box>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {params.alphas.map((alpha, idx) => {
                                            const sum = params.alphas.reduce((s, v) => s + v, 0);
                                            const showWarning = idx === params.alphas.length - 1 && Math.abs(sum - 1) > 0.001;
                                            return (
                                                <TextField
                                                    key={idx}
                                                    label={`\u03B1${idx + 1}`}
                                                    type="number"
                                                    size="small"
                                                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                                                    value={alpha}
                                                    error={showWarning}
                                                    helperText={showWarning ? `\u03A3=${sum.toFixed(3)}` : undefined}
                                                    onChange={(e): void => {
                                                        const val = parseFloat(e.target.value);
                                                        if (!isNaN(val)) {
                                                            const newAlphas = [...params.alphas];
                                                            newAlphas[idx] = val;
                                                            handleGroupParamChange(group.tagId, { model: 'alpha-factor', alphas: newAlphas });
                                                        }
                                                    }}
                                                    sx={{ width: 90 }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        α₁ = independent failures, αₘ = all-members-fail. Must sum to 1. Typical: α₁ ≈ 0.95–0.99.
                                    </Typography>
                                </Box>
                            )}

                            {/* MGL params */}
                            {params.model === 'mgl' && (
                                <Box>
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            label={'\u03B2'}
                                            type="number"
                                            size="small"
                                            inputProps={{ min: 0, max: 1, step: 0.01 }}
                                            value={params.beta}
                                            onChange={(e): void => {
                                                const val = parseFloat(e.target.value);
                                                if (!isNaN(val)) {
                                                    handleGroupParamChange(group.tagId, { ...params, beta: val });
                                                }
                                            }}
                                            sx={{ width: 100 }}
                                        />
                                        {group.memberCount >= 3 && (
                                            <TextField
                                                label={'\u03B3'}
                                                type="number"
                                                size="small"
                                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                                                value={params.gamma}
                                                onChange={(e): void => {
                                                    const val = parseFloat(e.target.value);
                                                    if (!isNaN(val)) {
                                                        handleGroupParamChange(group.tagId, { ...params, gamma: val });
                                                    }
                                                }}
                                                sx={{ width: 100 }}
                                            />
                                        )}
                                        {group.memberCount >= 4 && (
                                            <TextField
                                                label={'\u03B4'}
                                                type="number"
                                                size="small"
                                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                                                value={params.delta}
                                                onChange={(e): void => {
                                                    const val = parseFloat(e.target.value);
                                                    if (!isNaN(val)) {
                                                        handleGroupParamChange(group.tagId, { ...params, delta: val });
                                                    }
                                                }}
                                                sx={{ width: 100 }}
                                            />
                                        )}
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        {group.memberCount === 2
                                            ? 'β = P(shared cause | 1 failure). With 2 members, MGL is equivalent to Beta-Factor. Typical: β ≈ 0.05–0.15.'
                                            : 'β = P(shared cause | 1 failure). Typical: β ≈ 0.05–0.15, γ ≈ 0.3–0.5, δ ≈ 0.3–0.5.'}
                                    </Typography>
                                    {group.memberCount > 4 && (
                                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                            Warning: MGL supports up to 4 members (β, γ, δ). For this group of {group.memberCount}, only β, γ, and δ are applied — no additional parameters for the 5th+ member.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}

                {ccfModelType !== 'none' && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleApplyCCF}
                        sx={{ mt: 0.5 }}
                    >
                        Apply CCF
                    </Button>
                )}

                {ccfResult && (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography variant="body2">
                            Original top event P: {formatProbability(ccfResult.originalTopEventProbability)}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                            Adjusted top event P: {formatProbability(ccfResult.adjustedTopEventProbability)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {ccfResult.implicitCutSetCount} implicit CCF cut set{ccfResult.implicitCutSetCount !== 1 ? 's' : ''} added,
                            {' '}{ccfResult.groupsApplied} group{ccfResult.groupsApplied !== 1 ? 's' : ''} applied
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* CCF Result Table */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {ccfResult && (
                    <TableContainer component={Paper} elevation={0}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'index'}
                                            direction={sortField === 'index' ? sortDirection : 'asc'}
                                            onClick={(): void => handleSort('index')}
                                        >
                                            #
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'order'}
                                            direction={sortField === 'order' ? sortDirection : 'asc'}
                                            onClick={(): void => handleSort('order')}
                                        >
                                            Order
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'probability'}
                                            direction={sortField === 'probability' ? sortDirection : 'desc'}
                                            onClick={(): void => handleSort('probability')}
                                        >
                                            Probability
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'adjustedProbability'}
                                            direction={sortField === 'adjustedProbability' ? sortDirection : 'desc'}
                                            onClick={(): void => handleSort('adjustedProbability')}
                                        >
                                            Adj. Prob.
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Base Events</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedAdjustedCutSets.map((row, idx) => (
                                    <TableRow
                                        key={row.acs.isImplicitCCF ? `ccf-${idx}` : `orig-${row.acs.originalIndex}`}
                                        hover
                                        selected={selectedIndex === idx}
                                        onClick={(): void => onRowClick(idx)}
                                        sx={{
                                            cursor: 'pointer',
                                            ...(row.acs.isImplicitCCF && {
                                                bgcolor: 'warning.light',
                                                '&:hover': { bgcolor: 'warning.main' },
                                            }),
                                        }}
                                    >
                                        <TableCell>
                                            {row.displayIndex}
                                        </TableCell>
                                        <TableCell>{row.acs.order}</TableCell>
                                        <TableCell>
                                            {row.acs.isImplicitCCF ? '\u2014' : formatProbability(row.acs.originalProbability)}
                                        </TableCell>
                                        <TableCell>{formatProbability(row.acs.adjustedProbability)}</TableCell>
                                        <TableCell>
                                            {row.acs.isImplicitCCF && (
                                                <Chip
                                                    label="CCF"
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ mr: 0.5 }}
                                                />
                                            )}
                                            {getBaseEventNames(row.acs.baseEventIds)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!ccfResult && ccfModelType === 'none' && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Select a CCF model and configure parameters to begin analysis.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CCFAnalysis;
